// Single-person lookup form. Used to be a multi-row table that batched
// people for one POST; that was inefficient AND the wider form (with
// MVC inputs) made the table too cramped to use comfortably. Now:
//
//   • One person at a time.
//   • Submit immediately queues that person and clears the form.
//   • Below the form, a live "what will scrape" checklist shows which
//     of the eight sources will run based on which fields are filled.
//     Greyed by default, green ✓ once requirements are met *and the
//     value matches the expected format* — so a typo'd SSN or DL
//     can't trick the user into thinking that source will run.
//
// On batching: there's NO throughput advantage to batching submissions.
// Per-person scrapes are independent and the worker queue serializes
// regardless of how they were submitted. Submitting immediately means
// the worker can start scraping person #1 while the user fills out
// person #2 — strictly faster end-to-end + better UX (instant
// confirmation per submit).
import { useEffect, useState } from "react";
import type { PersonInput } from "../types";
import { getMe, listActiveUsersForDropdown, type UserSummary } from "../api";
import {
  formatDob,
  formatSsn,
  formatZip,
  formatDl,
  isValidDob,
  isValidSsn,
  isValidZip,
  isValidDl,
} from "../lib/identifierFormatters";

interface FormState extends PersonInput {
  /** UI-only: aliases as a single textarea string, one per line. Split
   *  into PersonInput.aliases on submit. */
  aliasesText: string;
}

function emptyForm(): FormState {
  return {
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    suffix: "",
    middleName: "",
    licenseNumber: "",
    ssn: "",
    zipCode: "",
    aliasesText: "",
    streetAddress: "",
    city: "",
    state: "NJ",
    phone: "",
    email: "",
    sex: null,
    contactUserId: null,
  };
}

interface Props {
  onSubmit: (people: PersonInput[]) => Promise<void>;
  submitting: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Source coverage checklist
// ─────────────────────────────────────────────────────────────────────────

interface CoverageRow {
  source: string;
  label: string;
  /** Plain-English description of what this scraper does. Shown as
   *  small help text under the label so a junior staffer knows why
   *  this one matters. */
  blurb: string;
  /** Returns true when the form has the inputs this source needs.
   *  Mirror of the server's missingInputsReason in externalFanout.ts —
   *  keep these two in sync when a new source ships. */
  needsMet: (f: FormState) => boolean;
  /** What's missing. Returned when needsMet is false so we can show
   *  "needs SSN" instead of just a grey checkmark. */
  missingHint?: (f: FormState) => string;
}

/** All name-based scrapes share these baseline requirements: name + a
 *  valid DOB (not just a partial entry). Without that, the worker
 *  can't disambiguate hits or solve CAPTCHAs that ask for DOB. */
function hasBaselineId(f: FormState): boolean {
  return Boolean(f.firstName.trim()) && Boolean(f.lastName.trim()) && isValidDob(f.dateOfBirth);
}

function baselineMissingHint(f: FormState): string {
  if (!f.firstName.trim() || !f.lastName.trim()) return "needs first + last name";
  if (!f.dateOfBirth.trim()) return "needs DOB";
  return "needs valid DOB (YYYY-MM-DD)";
}

const COVERAGE: CoverageRow[] = [
  {
    source: "mpa",
    label: "Municipal Court",
    blurb: "All NJ municipal cases, tickets, low-grade crim, suspended-license stops.",
    needsMet: hasBaselineId,
    missingHint: baselineMissingHint,
  },
  {
    source: "pgpa",
    label: "Superior Court",
    blurb: "Indictable / felony cases statewide.",
    needsMet: hasBaselineId,
    missingHint: baselineMissingHint,
  },
  {
    source: "jocpa",
    label: "Court Judgments",
    blurb: "Filed conviction / acquittal / dismissal judgments (June 2017+).",
    needsMet: hasBaselineId,
    missingHint: baselineMissingHint,
  },
  {
    source: "doc",
    label: "NJ Prison",
    blurb: "Department of Corrections inmate records.",
    needsMet: hasBaselineId,
    missingHint: baselineMissingHint,
  },
  {
    source: "mvc-surcharge",
    label: "MVC Surcharges",
    blurb: "Outstanding NJ surcharges (often the biggest debt blocking license restoration).",
    // Strict — must be a valid DL shape, not just non-empty. The scraper
    // can't recover from a typo in the DL field; better to keep the row
    // grey until the value matches the standard format.
    needsMet: (f) => isValidDl(f.licenseNumber ?? ""),
    missingHint: (f) => {
      if (!f.licenseNumber?.trim()) return "needs driver's license #";
      return "DL # doesn't look right (expect 8–20 letters/digits)";
    },
  },
  {
    source: "mvc-restoration",
    label: "MVC Restoration",
    blurb: "Outstanding restoration fees due before a suspended license can be restored.",
    needsMet: (f) =>
      isValidDl(f.licenseNumber ?? "") &&
      isValidSsn(f.ssn ?? "") &&
      isValidZip(f.zipCode ?? ""),
    missingHint: (f) => {
      const missing: string[] = [];
      if (!isValidDl(f.licenseNumber ?? "")) missing.push("DL #");
      if (!isValidSsn(f.ssn ?? "")) missing.push("SSN");
      if (!isValidZip(f.zipCode ?? "")) missing.push("ZIP");
      return `needs ${missing.join(" + ")}`;
    },
  },
  // ── Document-readiness rows. Not scrapers — these row toggles tell
  //    the operator which downstream PDFs / letters will auto-fill end-
  //    to-end vs. need manual completion later. Same gray/green style
  //    as the scraper rows; matches Damon's request for a unified
  //    readiness signal across both data sources and outputs.
  {
    source: "doc-warrant-letter",
    label: "Warrant letter (auto-fill)",
    blurb: "Pre-filled NJRC warrant-recall letter with participant return address, ready to print + send if a warrant comes back on this person.",
    needsMet: (f) =>
      Boolean(f.streetAddress?.trim()) &&
      Boolean(f.city?.trim()) &&
      isValidZip(f.zipCode ?? ""),
    missingHint: (f) => {
      const missing: string[] = [];
      if (!f.streetAddress?.trim()) missing.push("street address");
      if (!f.city?.trim()) missing.push("city");
      if (!isValidZip(f.zipCode ?? "")) missing.push("ZIP");
      return `needs ${missing.join(" + ")}`;
    },
  },
  {
    source: "doc-do21",
    label: "DO-21 driver abstract (auto-fill)",
    blurb: "Pre-filled NJ MVC driver-history-abstract application, fills name, DOB, address, DL #. Client signs + mails.",
    needsMet: (f) =>
      Boolean(f.streetAddress?.trim()) &&
      Boolean(f.city?.trim()) &&
      Boolean((f.state ?? "NJ").trim()) &&
      isValidZip(f.zipCode ?? ""),
    missingHint: (f) => {
      const missing: string[] = [];
      if (!f.streetAddress?.trim()) missing.push("street");
      if (!f.city?.trim()) missing.push("city");
      if (!(f.state ?? "NJ").trim()) missing.push("state");
      if (!isValidZip(f.zipCode ?? "")) missing.push("ZIP");
      return `needs ${missing.join(" + ")}`;
    },
  },
];

function CoverageChecklist({ form }: { form: FormState }): JSX.Element {
  const metCount = COVERAGE.filter((c) => c.needsMet(form)).length;
  return (
    <div
      className="mt-6 rounded-xl border p-4 sm:p-5"
      style={{
        borderColor: "var(--color-line)",
        background: "var(--color-bg-card)",
      }}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: "var(--color-text-dim)", fontFamily: "var(--font-mono)" }}
        >
          Sources this submission will hit
        </div>
        <div className="text-[11px]" style={{ color: "var(--color-text-muted)" }}>
          <strong style={{ color: metCount === COVERAGE.length ? "var(--color-sage-hi)" : "var(--color-amber-hi)" }}>
            {metCount}
          </strong>
          {" "}of {COVERAGE.length} ready
        </div>
      </div>
      <ul className="grid gap-1.5 sm:grid-cols-2" style={{ listStyle: "none", padding: 0 }}>
        {COVERAGE.map((c) => {
          const ready = c.needsMet(form);
          const hint = !ready && c.missingHint ? c.missingHint(form) : null;
          return (
            <li
              key={c.source}
              className="flex items-baseline gap-2 py-1"
              title={c.blurb}
            >
              <span
                aria-hidden="true"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  width: 14,
                  textAlign: "center",
                  color: ready ? "var(--color-sage-hi)" : "var(--color-text-dim)",
                  flexShrink: 0,
                }}
              >
                {ready ? "✓" : "○"}
              </span>
              <span
                style={{
                  fontSize: 13,
                  color: ready ? "var(--color-sage-hi)" : "var(--color-text-dim)",
                  fontWeight: ready ? 500 : 400,
                  textDecoration: ready ? "none" : "none",
                }}
              >
                {c.label}
              </span>
              {hint && (
                <span
                  className="text-[11px]"
                  style={{ color: "var(--color-text-dim)", opacity: 0.85 }}
                >
                  ({hint})
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {metCount < COVERAGE.length && (
        <p
          className="mt-3 text-[12px]"
          style={{ color: "var(--color-text-muted)", lineHeight: 1.5 }}
        >
          Submit anyway to scrape what'
          You can always re-submit later with more info to fill the gaps.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Form
// ─────────────────────────────────────────────────────────────────────────

export function PersonEntryTable({ onSubmit, submitting }: Props): JSX.Element {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  const update = (patch: Partial<FormState>): void => {
    setForm((f) => ({ ...f, ...patch }));
    if (error) setError(null);
  };

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    if (!form.firstName.trim() || !form.lastName.trim() || !form.dateOfBirth.trim()) {
      setError("First name, last name, and date of birth are required.");
      return;
    }
    if (!isValidDob(form.dateOfBirth)) {
      setError(`Date of birth must be a valid YYYY-MM-DD (got "${form.dateOfBirth}").`);
      return;
    }
    // Aliases now comma-separated. Allow leading/trailing whitespace per
    // alias and drop empty entries (caused by trailing commas, double
    // commas, etc.).
    const aliasList = form.aliasesText
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    // Strip SSN dashes before sending — the worker normalizes back to
    // digits anyway, but keeping the wire-format clean is nicer.
    const ssnDigits = form.ssn?.replace(/[-\s]/g, "") ?? "";
    const person: PersonInput = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      dateOfBirth: form.dateOfBirth.trim(),
      // Optional fields — strip empty/whitespace, drop if absent so the
      // server doesn't see blank strings as values.
      suffix: form.suffix?.trim() || undefined,
      middleName: form.middleName?.trim() || undefined,
      licenseNumber: form.licenseNumber?.trim() || undefined,
      ssn: ssnDigits || undefined,
      zipCode: form.zipCode?.trim() || undefined,
      aliases: aliasList.length > 0 ? aliasList : undefined,
      // Address & contact — only sent when populated, so the server's
      // intake handler skips writing petitioner_details unless we
      // captured at least one of these.
      streetAddress: form.streetAddress?.trim() || undefined,
      city: form.city?.trim() || undefined,
      state: form.state?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      email: form.email?.trim() || undefined,
      sex: form.sex || undefined,
      contactUserId: form.contactUserId ?? undefined,
    };
    await onSubmit([person]);
    // Clear the form for the next person + return focus to first name.
    setForm(emptyForm());
    requestAnimationFrame(() => {
      document.querySelector<HTMLInputElement>('input[data-form-first]')?.focus();
    });
  };

  return (
    <div className="card-elevated lookup-form-card">
      <div className="border-b border-[color:var(--color-border)] px-6 py-4">
        <h2 className="text-lg font-semibold text-[color:var(--color-ink)]">Look up one person</h2>
        <p className="mt-0.5 text-sm text-[color:var(--color-ink-soft)]">
          Submit queues this person immediately. The form clears so you can add the next.
        </p>
      </div>

      <div className="px-4 py-5 sm:px-6">
        {/* Required fields */}
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_minmax(160px,200px)]">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              First name <span style={{ color: "var(--color-rose-hi)" }}>*</span>
            </label>
            <input
              data-form-first
              className="input"
              placeholder="Jane"
              autoComplete="off"
              value={form.firstName}
              onChange={(e) => update({ firstName: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Last name <span style={{ color: "var(--color-rose-hi)" }}>*</span>
            </label>
            <input
              className="input"
              placeholder="Doe"
              autoComplete="off"
              value={form.lastName}
              onChange={(e) => update({ lastName: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Date of birth <span style={{ color: "var(--color-rose-hi)" }}>*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              className="input mono w-full"
              placeholder="YYYY-MM-DD"
              autoComplete="bday"
              maxLength={10}
              value={form.dateOfBirth}
              onChange={(e) => update({ dateOfBirth: formatDob(e.target.value) })}
            />
          </div>
        </div>

        {/* Optional fields — always visible on the single-person form so the
            checklist below is actionable without an extra click. */}
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Middle name
            </label>
            <input
              className="input"
              placeholder="optional"
              autoComplete="additional-name"
              value={form.middleName ?? ""}
              onChange={(e) => update({ middleName: e.target.value })}
              title="Filled into MPA's Middle field. Worker also runs a no-middle variant alongside, so providing it never narrows results."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Suffix
            </label>
            <input
              className="input"
              placeholder="Jr / Sr / III"
              autoComplete="honorific-suffix"
              value={form.suffix ?? ""}
              onChange={(e) => update({ suffix: e.target.value })}
              title="Name suffix. Triggers extra MPA name searches (with / without suffix in the last-name field)."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              ZIP code
            </label>
            <input
              className="input mono"
              placeholder="5 digits"
              autoComplete="postal-code"
              inputMode="numeric"
              maxLength={5}
              value={form.zipCode ?? ""}
              onChange={(e) => update({ zipCode: formatZip(e.target.value) })}
              title="Required by MVC restoration-fees lookup."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Driver's license / ID #
            </label>
            <input
              className="input mono"
              placeholder="NJ DL or state ID"
              autoComplete="off"
              maxLength={20}
              value={form.licenseNumber ?? ""}
              onChange={(e) => update({ licenseNumber: formatDl(e.target.value) })}
              title="Required by MVC surcharge + restoration-fees scrapers. Auto-uppercased; punctuation stripped. Auto-fills the DO-21 driver-abstract form."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              SSN
            </label>
            <input
              className="input mono"
              placeholder="XXX-XX-XXXX"
              autoComplete="off"
              inputMode="numeric"
              maxLength={11}
              value={form.ssn ?? ""}
              onChange={(e) => update({ ssn: formatSsn(e.target.value) })}
              title="Required by the MVC restoration-fees lookup. Stored only on the server, never sent off-platform. Dashes added automatically as you type."
            />
          </div>
          <div className="flex flex-col gap-1 sm:col-span-3">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Also known as <span className="ml-1 text-[10px] normal-case tracking-normal opacity-70">— comma-separated</span>
            </label>
            <input
              type="text"
              className="input mono"
              placeholder="Robert Smith, Smith-Jones, O'Brien"
              autoComplete="off"
              value={form.aliasesText}
              onChange={(e) => update({ aliasesText: e.target.value })}
              title="Each comma-separated entry is searched as another MPA name variant. Use 'First Last' or just 'Last'. Punctuation variants are auto-tried only when the name actually contains them."
            />
          </div>

          {/* Address + contact — auto-fills warrant letter, DO-21
              driver-abstract form, fine-waiver letter, and the
              expungement Form 9. Capturing them once at intake means
              every downstream document is ready without separate
              data-entry. All optional. State defaults to NJ. */}
          <div className="flex flex-col gap-1 sm:col-span-3">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Street address
            </label>
            <input
              className="input"
              placeholder="123 Main St Apt 4B (optional)"
              autoComplete="address-line1"
              value={form.streetAddress ?? ""}
              onChange={(e) => update({ streetAddress: e.target.value })}
              title="Auto-fills warrant letter, DO-21 driver-abstract form, fine-waiver letter, and expungement Form 9."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              City
            </label>
            <input
              className="input"
              placeholder="Paterson"
              autoComplete="address-level2"
              value={form.city ?? ""}
              onChange={(e) => update({ city: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              State
            </label>
            <input
              className="input mono"
              placeholder="NJ"
              autoComplete="address-level1"
              maxLength={2}
              value={form.state ?? "NJ"}
              onChange={(e) => update({ state: e.target.value.toUpperCase() })}
              title="Defaults to NJ: change only for out-of-state clients."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Sex
            </label>
            <select
              className="input"
              value={form.sex ?? ""}
              onChange={(e) =>
                update({ sex: (e.target.value === "" ? null : e.target.value as "M" | "F" | "X") })
              }
              title="Used for letter pronouns and the DO-21 form. Optional."
            >
              <option value="">— optional —</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="X">Other / unspecified</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Phone
            </label>
            <input
              className="input"
              placeholder="(973) 555-0100"
              autoComplete="tel"
              inputMode="tel"
              value={form.phone ?? ""}
              onChange={(e) => update({ phone: e.target.value })}
              title="Auto-fills warrant letter + DO-21."
            />
          </div>
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              Email
            </label>
            <input
              type="email"
              className="input"
              placeholder="optional"
              autoComplete="email"
              value={form.email ?? ""}
              onChange={(e) => update({ email: e.target.value })}
              title="Auto-fills warrant letter + DO-21."
            />
          </div>
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-dim)]">
              NJRC Contact
            </label>
            <NjrcContactPicker
              value={form.contactUserId ?? null}
              onChange={(uid) => update({ contactUserId: uid })}
            />
          </div>
        </div>

        {/* Live coverage checklist — updates as the user types. Greys out
            sources whose required fields aren't filled yet, greens up
            with a checkmark once they're satisfied. Lets the user see at
            a glance what they'll get + what's still missing. */}
        <CoverageChecklist form={form} />

        {error && (
          <div className="mt-4 rounded-lg bg-[color:var(--color-warn-soft)] px-3 py-2 text-sm text-[color:var(--color-warn)]">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-3 flex-wrap">
          <span
            className="text-[11px]"
            style={{ color: "var(--color-text-dim)", fontFamily: "var(--font-mono)", letterSpacing: "0.12em", textTransform: "uppercase" }}
          >
            Submits immediately to the queue
          </span>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? "Queueing…" : "Queue lookup →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── NJRC Contact picker ───────────────────────────────────────────────────
//
// Dropdown of all active users. Defaults to the currently logged-in user
// — i.e. "who's adding this intake?" is implicitly the contact unless
// someone says otherwise. Re-uses the cookie-session /api/auth/me so we
// don't need to thread user state down from a parent.

function NjrcContactPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (uid: number | null) => void;
}): JSX.Element {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([listActiveUsersForDropdown(), getMe()])
      .then(([list, me]) => {
        if (!alive) return;
        setUsers(list);
        // Default selection to the current logged-in user when the
        // caller hasn't set one yet. Caller stays in control after that.
        if (value === null && me) {
          onChange(me.id);
        }
      })
      .catch(() => {/* leave dropdown empty; selecting is still optional */})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <select
      className="input"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      title="Primary NJRC staff contact for this participant. Defaults to you."
      disabled={loading}
    >
      <option value="">— unassigned —</option>
      {users.map((u) => (
        <option key={u.id} value={u.id}>
          {u.first_name} {u.last_name} (@{u.login})
        </option>
      ))}
    </select>
  );
}
